import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, ArrowRight } from 'lucide-react';
import { FaSeedling, FaLeaf, FaTree, FaSun, FaUserGraduate, FaBookOpen, FaPhoneAlt, FaCalendarAlt, FaCheckCircle, FaSync, FaGraduationCap } from 'react-icons/fa';
import { MdLocalFlorist, MdLandscape } from 'react-icons/md';
import './ServiceDetail.css';

const ACCENT = '#558b2f';
const ACCENT_LIGHT = '#8bc34a';
const ACCENT_RGB = '85,139,47';

const benefits = [
  {
    emoji: <FaSeedling size={22} />,
    title: 'Practical Plant Care Training',
    desc: 'Learn watering, pruning, repotting, nutrition management, and everyday plant care techniques.'
  },
  {
    emoji: <MdLocalFlorist size={22} />,
    title: 'Hands-On Learning Experience',
    desc: 'Gain practical gardening knowledge through demonstrations and real plant care activities.'
  },
  {
    emoji: <FaLeaf size={22} />,
    title: 'Indoor & Outdoor Plant Knowledge',
    desc: 'Understand how to select, maintain, and grow healthy plants in different environments.'
  },
  {
    emoji: <FaSun size={22} />,
    title: 'Home Gardening Techniques',
    desc: 'Learn simple methods to create and maintain attractive home gardens and green spaces.'
  },
  {
    emoji: <FaUserGraduate size={22} />,
    title: 'Guidance From Nursery Experts',
    desc: 'Receive practical advice and support from experienced plant care professionals.'
  },
  {
    emoji: <FaBookOpen size={22} />,
    title: 'Beginner-Friendly Sessions',
    desc: 'Easy-to-understand training suitable for beginners, hobby gardeners, and plant enthusiasts.'
  },
];

const steps = [
  {
    num: '01',
    icon: <FaPhoneAlt size={20} />,
    title: 'Connect With Our Team',
    desc: 'Contact us to learn about available gardening and plant care training sessions.'
  },
  {
    num: '02',
    icon: <FaCalendarAlt size={20} />,
    title: 'Select Your Program',
    desc: 'Choose a training session based on your interests and gardening goals.'
  },
  {
    num: '03',
    icon: <FaLeaf size={20} />,
    title: 'Participate & Learn',
    desc: 'Attend practical sessions covering plant care, gardening, and maintenance techniques.'
  },
  {
    num: '04',
    icon: <MdLocalFlorist size={20} />,
    title: 'Apply Your Knowledge',
    desc: 'Use your newly learned skills to grow and maintain healthy plants confidently.'
  },
];

const stats = [
  { val: <FaSeedling size={28} />, label: 'Plant Care Learning' },
  { val: <MdLocalFlorist size={28} />, label: 'Hands-On Training' },
  { val: <FaLeaf size={28} />, label: 'Gardening Skills' },
  { val: <FaUserGraduate size={28} />, label: 'Expert Guidance' },
];

export default function TrainingProgramsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Training Programs — Nisarg Nursery';
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.sdp-observe').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="sdp-wrapper"
      style={{ '--accent': ACCENT, '--accent-light': ACCENT_LIGHT, '--accent-dark': '#0d47a1', '--accent-rgb': ACCENT_RGB }}
    >
      {/* ════ HERO ════ */}
      <section className="sdp-hero">
        <div className="sdp-hero-bg">
          <img src="/images/bonsai.jpg" alt="Training Programs" className="sdp-hero-img" />
          <div className="sdp-hero-overlay" />
        </div>
        <div className="sdp-hero-content">
          <nav className="sdp-breadcrumb">
            <Link to="/">Home</Link> › <Link to="/services">Services</Link> › <span>Training Programs</span>
          </nav>
          <div className="sdp-service-tag">
            <GraduationCap size={16} /> <FaLeaf /> Gardening & Plant Care Training
          </div>
          <span className="sdp-hero-icon"><GraduationCap size={56} strokeWidth={1.5} /></span>
          <h1 className="sdp-hero-title">
            Practical <span className="accent-word">Plant Care &
              Home Gardening</span> Training
          </h1>
          <p className="sdp-hero-desc">
            Learn plant care, home gardening, organic growing methods, and garden maintenance through practical training sessions guided by experienced nursery professionals.
          </p>
          <div className="sdp-hero-cta">
            <Link to="/contact" className="sdp-btn-primary">
              Enroll Now <ArrowRight size={16} />
            </Link>
            <Link to="/services" className="sdp-btn-outline">← Back to Services</Link>
          </div>
        </div>
        <div className="sdp-hero-scroll">
          <span>SCROLL</span>
          <div className="sdp-scroll-line" />
        </div>
      </section>

      {/* ── STATS ── */}
      <div className="sdp-stats">
        {stats.map(s => (
          <div key={s.label} className="sdp-stat sdp-observe">
            <span className="sdp-stat-val">{s.val}</span>
            <span className="sdp-stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      <section className="sdp-section sdp-overview">
        <div className="sdp-section-inner">
          <div className="sdp-overview-grid">
            <div className="sdp-overview-left sdp-observe">
              <div className="sdp-overview-img-wrap">
                <img src="/images/hanging5.jpg" alt="Training" className="sdp-overview-img" />
                <div className="sdp-overview-badge">
                  <div className="sdp-overview-badge-dot" />
                  Enrollments Open
                </div>
              </div>
            </div>
            <div className="sdp-overview-right sdp-observe">
              <div className="sdp-section-tag"><FaLeaf /> Learn Modern Gardening</div>
              <h2 className="sdp-section-title">Build Confidence In<br />Plant Care & Gardening</h2>
              <div className="sdp-overview-text">
                <p>Our training programs are designed for plant lovers, homeowners, gardening enthusiasts, and beginners who want to learn practical plant care techniques and sustainable gardening methods.</p>
                <p>From indoor plants and outdoor gardens to soil preparation, watering techniques, plant nutrition, and seasonal care, participants gain real-world gardening knowledge through practical learning experiences.</p>
              </div>
              <div className="sdp-features-list">
                {[
                  'Plant Care Basics',
                  'Home Gardening',
                  'Organic Gardening',
                  'Practical Learning',
                  'Expert Guidance',
                  'Garden Maintenance'
                ].map(f => (
                  <div key={f} className="sdp-feature-item">
                    <div className="sdp-feature-icon"><FaCheckCircle /></div>
                    {f}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className="sdp-section sdp-observe">
        <div className="sdp-section-inner">
          <div className="sdp-section-tag"><FaCheckCircle /> Why Choose Our Training</div>
          <h2 className="sdp-section-title">What You'll Learn<br />From Our Programs</h2>
          <div className="sdp-benefits-grid">
            {benefits.map(b => (
              <div key={b.title} className="sdp-benefit-card sdp-observe">
                <span className="sdp-benefit-emoji">{b.emoji}</span>
                <h3 className="sdp-benefit-title">{b.title}</h3>
                <p className="sdp-benefit-desc">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="sdp-section sdp-process sdp-observe">
        <div className="sdp-section-inner">
          <div className="sdp-section-tag"><FaSync /> How It Works</div>
          <h2 className="sdp-section-title">Start Your Gardening<br />Learning Journey</h2>
          <div className="sdp-process-steps">
            {steps.map(s => (
              <div key={s.num} className="sdp-step sdp-observe">
                <div className="sdp-step-num">{s.num}</div>
                <div className="sdp-step-icon">{s.icon}</div>
                <h3 className="sdp-step-title">{s.title}</h3>
                <p className="sdp-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <div className="sdp-cta sdp-observe">
        <span className="sdp-cta-icon"><FaGraduationCap size={28} /></span>
        <h2 className="sdp-cta-title">Ready To Grow Your Gardening Skills?</h2>
        <p className="sdp-cta-desc">Join our practical gardening and plant care training programs and learn how to create healthy, thriving green spaces with confidence.</p>
        <div className="sdp-cta-btns">
          <Link to="/contact" className="sdp-btn-primary">
            Join a Program
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
          </Link>
          <Link to="/services" className="sdp-btn-outline">← All Services</Link>
        </div>
      </div>
    </div>
  );
}
